import { useEffect, useState } from "react";
import { apiGet } from "@/shared/api";

type SystemInfoMigration = {
  migration_status?: "ok" | "ahead" | "error" | "unknown";
  migration_detail?: string;
};

/**
 * Banner persistente "L'app e' obsoleta" mostrato in cima a tutte le pagine
 * quando il backend rileva DB rev > code rev.
 *
 * Trigger: ``GET /api/system/info`` ritorna ``migration_status === "ahead"``.
 *
 * Audit 2026-05-14: senza questo banner l'utente vedeva solo "Backend non
 * avviabile" senza capire perche' (alembic non riconosceva la rev DB).
 *
 * ⛔ 2026-08-11: tolto il CTA "Scarica nuovo installer". Il desktop Electron
 * non esiste piu' dal 2026-07-17 e da browser non si installa niente: quel
 * bottone portava l'utente su un feed di aggiornamento che non lo riguarda.
 * Oggi lo stato "ahead" nasce da un rilascio all'indietro (immagine piu'
 * vecchia del DB) e il rimedio e' il redeploy, non un gesto dell'utente.
 */
export function OutdatedAppBanner() {
  const [info, setInfo] = useState<SystemInfoMigration | null>(null);

  useEffect(() => {
    let cancelled = false;
    void apiGet<SystemInfoMigration>("/api/system/info")
      .then((data) => {
        if (!cancelled) setInfo(data);
      })
      .catch(() => {
        // Backend down o errore di rete: il SyncStatusPill esistente mostra
        // gia' "Backend irraggiungibile", non duplichiamo qui.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!info || info.migration_status !== "ahead") return null;

  return (
    <div className="border-b border-amber/40 bg-amber-soft px-5 py-2.5 text-[12.5px] text-ink">
      <div className="mx-auto flex max-w-[1200px] items-center gap-3">
        <svg
          className="h-4 w-4 flex-shrink-0 text-ink"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
        <div className="flex-1">
          <strong className="font-semibold">
            Questa versione dell'app è più vecchia del database.
          </strong>{" "}
          {info.migration_detail ||
            "Il database è stato aggiornato da una versione più recente del codice: alcune funzioni potrebbero non funzionare."}{" "}
          Non c'è niente da installare: ricarica la pagina dopo il rilascio dell'immagine
          aggiornata. Se il messaggio resta, avvisa chi gestisce il deploy.
        </div>
      </div>
    </div>
  );
}
